<template>
    <div class="layout-root" :class="{ 'nav-open': showSidebar }">
        <div class="main-content wrapper">
            <Navbar
                v-if="userSelf"
                :systemTitle="sidebarTitle"
                :pageTitle="pageTitle"
                :userSelf="userSelf"
                @toggle-sidebar="toggleSidebar"
            />
            <side-bar
                :logo="systemLogo"
                :title="sidebarTitle"
                :background-color="sidebarBackgroundColor"
                :active-color="sidebarActiveColor"
                :sidebar-links="sidebarLinks"
            />
            <div class="sidebar-backdrop" @click="closeSidebar"></div>
            <div>
                <div class="content">
                    <div class="container-fluid">
                        <router-view v-slot="{ Component }">
                            <transition name="fade" mode="out-in">
                                <component
                                    :is="Component"
                                    :key="$route.fullPath"
                                />
                            </transition>
                        </router-view>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, computed, watch, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Navbar from '@/components/Navbar/Navbar.vue';
import { userSelf as fetchUserSelf } from '@/api/users';

export default {
    components: {
        Navbar,
    },
    setup() {
        const { t } = useI18n();
        const store = useStore();
        const route = useRoute();

        const userSelf = ref(null);
        const menuItems = ref([]);
        const pageTitle = ref('');

        function translateItems(items) {
            return items.map(it => ({
                name: t(`sidebar.sidebar_msg__${it.name}`),
                path: it.path,
                icon: it.icon,
                children: it.children ? translateItems(it.children) : undefined,
            }));
        }

        function findInTree(list, routePath) {
            for (const n of list) {
                if (n.path === routePath) return n;
                if (n.children) {
                    const f = findInTree(n.children, routePath);
                    if (f) return f;
                }
            }
            return null;
        }

        const getPageTitle = routePath => {
            let node = findInTree(menuItems.value, routePath);
            if (!node) {
                // 改成使用 route.fullPath 尋找
                node = findInTree(menuItems.value, route.fullPath);
            }
            return node
                ? t(`sidebar.sidebar_msg__${node.name}`)
                : 'Unknown Page';
        };

        const fetchSidebarLinks = () => {
            const translated = translateItems(menuItems.value);
            setSidebarLinks(translated);
        };

        const getUserSelf = async () => {
            try {
                await new Promise(r => setTimeout(r, 100));
                const { user_data, menu_items } = await fetchUserSelf();
                userSelf.value = user_data;
                store.dispatch('auth/updateUserSelf', user_data);
                menuItems.value = menu_items;
                fetchSidebarLinks();
                pageTitle.value = getPageTitle(route.fullPath);
            } catch (e) {
                console.error('Error fetching user self:', e);
            }
        };

        const systemLogo = computed(() => {
            const logoBase64 = store.getters['system/logo'];
            if (logoBase64) {
                return `data:image/png;base64,${logoBase64}`;
            }
            return `${process.env.BASE_URL}assets/img/dhc-logo.png`;
        });

        const sidebarTitle = computed(() => {
            return store.getters['system/title'] || 'Club Assist SaaS';
        });

        const sidebarBackgroundColor = ref('black');
        const sidebarActiveColor = ref('primary');

        const showSidebar = computed(() => store.state.sidebar.showSidebar);
        const sidebarLinks = computed(() => store.state.sidebar.sidebarLinks);

        const toggleSidebar = () => {
            store.dispatch('sidebar/toggleSidebar');
        };
        const setSidebarLinks = links => {
            store.dispatch('sidebar/setSidebarLinks', links);
        };
        const closeSidebar = () => {
            store.dispatch('sidebar/setSidebar', false);
        };

        onMounted(async () => {
            await getUserSelf();
        });

        watchEffect(() => {
            pageTitle.value = getPageTitle(route.fullPath);
        });
        watch(
            () => route.fullPath,
            () => {
                const isMobile =
                    typeof window !== 'undefined' &&
                    window.matchMedia('(max-width: 991px)').matches;
                if (isMobile && showSidebar.value) {
                    closeSidebar();
                }
            }
        );

        return {
            systemLogo,
            sidebarTitle,
            sidebarBackgroundColor,
            sidebarActiveColor,
            showSidebar,
            sidebarLinks,
            toggleSidebar,
            closeSidebar,
            pageTitle,
            userSelf,
        };
    },
};
</script>

<style lang="scss">
.layout-root {
    min-height: 100vh;
}

.layout-root .main-content {
    flex-grow: 1;
    min-height: 100vh;
    transition: margin-left 0.25s ease;
    will-change: margin-left;
}

.layout-root .content {
    padding: 20px;
}

.layout-root .sidebar {
    transform: translateX(-100%);
    transition: transform 0.25s ease;
    will-change: transform;
}

.layout-root.nav-open .sidebar {
    transform: translateX(0);
}

.layout-root .sidebar-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;
    z-index: 1030;
}

@media (min-width: 992px) {
    .layout-root .main-content {
        margin-left: 0;
    }
    .layout-root.nav-open .main-content {
        margin-left: 260px;
    }
    .layout-root .sidebar {
        position: fixed;
        top: 0;
        left: 0;
        height: 100vh;
        width: 260px;
    }
    .layout-root .sidebar-backdrop {
        display: none;
    }
}

@media (max-width: 991px) {
    .layout-root .main-content {
        margin-left: 0;
    }
    .layout-root.nav-open .main-content {
        margin-left: 0;
        transform: none;
    }
    .layout-root .wrapper .sidebar {
        transform: translateX(-100%) !important;
        z-index: 1040;
    }
    .layout-root.nav-open .wrapper .sidebar {
        transform: translateX(0) !important;
    }
    .layout-root.nav-open .sidebar-backdrop {
        opacity: 1;
        pointer-events: auto;
    }
}
</style>
